<template>
  <teleport to="body">
    <transition name="h-drawer-fade">
      <div
        v-if="modelValue"
        class="h-drawer-scrim"
        @click.self="requestClose('scrim')"
      >
        <transition name="h-drawer-slide" appear>
          <aside
            v-if="modelValue"
            ref="panel"
            class="h-drawer"
            role="dialog"
            aria-modal="true"
            :aria-label="title"
            @keydown.esc.prevent="requestClose('esc')"
            @keydown.tab="onTab"
          >
            <header class="h-drawer__head">
              <slot name="header">
                <h3 class="h-drawer__title">{{ title }}</h3>
              </slot>
              <button type="button" class="h-drawer__x" aria-label="Close" @click="requestClose('button')">✕</button>
            </header>

            <div class="h-drawer__body">
              <slot />
            </div>

            <footer v-if="$slots.footer" class="h-drawer__foot">
              <slot name="footer" />
            </footer>
          </aside>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
// Right slide-over drawer — inspect a record without leaving the list.
// Scrim + focus trap + Esc-to-close + scrim-click-to-close. header/footer slots.
// v-model:open (modelValue) controls visibility.
export default {
  name: 'HermesDrawer',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    // Set false to require an explicit control (destructive contexts).
    closeOnScrim: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
  },
  emits: ['update:modelValue', 'close'],
  data() {
    return { lastFocused: null };
  },
  watch: {
    modelValue(open) {
      if (open) this.onOpen();
      else this.onClose();
    },
  },
  mounted() {
    if (this.modelValue) this.onOpen();
  },
  beforeUnmount() {
    this.releaseBodyScroll();
  },
  methods: {
    requestClose(source) {
      if (source === 'scrim' && !this.closeOnScrim) return;
      if (source === 'esc' && !this.closeOnEsc) return;
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    onOpen() {
      this.lastFocused = document.activeElement;
      document.documentElement.style.overflow = 'hidden';
      this.$nextTick(() => this.focusFirst());
    },
    onClose() {
      this.releaseBodyScroll();
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    },
    releaseBodyScroll() {
      document.documentElement.style.overflow = '';
    },
    focusable() {
      if (!this.$refs.panel) return [];
      return Array.from(
        this.$refs.panel.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    },
    focusFirst() {
      const f = this.focusable();
      if (f.length) f[0].focus();
      else if (this.$refs.panel) this.$refs.panel.focus();
    },
    onTab(e) {
      const f = this.focusable();
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.h-drawer-scrim {
  position: fixed; inset: 0; z-index: 1200;
  background: var(--h-bg-overlay, rgba(15, 23, 42, 0.55));
  display: flex; justify-content: flex-end;
}
.h-drawer {
  width: 400px; max-width: 92vw; height: 100%;
  background: var(--h-bg-card, #fff);
  box-shadow: var(--h-shadow-pop);
  border-left: 1px solid var(--h-border, #E2E8F0);
  display: flex; flex-direction: column;
  font-family: var(--h-font-body, system-ui);
  outline: none;
}
.h-drawer__head {
  padding: 18px 20px; border-bottom: 1px solid var(--h-border, #E2E8F0);
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.h-drawer__title { margin: 0; font-size: 15px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-drawer__x { border: none; background: none; color: var(--h-text-tertiary, #94A3B8); cursor: pointer; font-size: 18px; line-height: 1; padding: 4px; border-radius: var(--h-radius, 6px); }
.h-drawer__x:hover { color: var(--h-text-primary, #0F172A); }
.h-drawer__x:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-drawer__body { padding: 18px 20px; overflow: auto; flex: 1; }
.h-drawer__foot { padding: 16px 20px; border-top: 1px solid var(--h-border, #E2E8F0); background: var(--h-bg-subtle, #F1F5F9); display: flex; justify-content: flex-end; gap: 10px; }

.h-drawer-fade-enter-active, .h-drawer-fade-leave-active { transition: opacity 0.18s ease; }
.h-drawer-fade-enter-from, .h-drawer-fade-leave-to { opacity: 0; }
.h-drawer-slide-enter-active, .h-drawer-slide-leave-active { transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
.h-drawer-slide-enter-from, .h-drawer-slide-leave-to { transform: translateX(100%); }
</style>
